import React from 'react'
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { useController, UseControllerProps } from 'react-hook-form'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { ChevronsUpDown } from 'lucide-react'
import { cn } from '@/lib/utils'

interface Currency {
  code: string
  displayName: string
}

interface Props {
  label?: string
  placeholder?: string
  currenciesData: Currency[]
  showDoubleChevron?: boolean
}

export function InputCurrencyForm(props: Props & UseControllerProps) {
  const {
    control,
    name,
    label,
    placeholder,
    currenciesData,
    rules,
    showDoubleChevron = false,
  } = props
  const { field, fieldState } = useController(props)
  const isRequired =
    rules?.required !== undefined || rules?.validate !== undefined

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    let amount = 0

    if (value && value.trim() !== '') {
      const parsedValue = parseFloat(value)
      if (!isNaN(parsedValue)) {
        const decimalPart = value.split('.')[1]
        if (decimalPart && decimalPart.length > 10) {
          const parts = value.split('.')
          const limited = parseFloat(
            `${parts[0]}.${parts[1]?.substring(0, 10)}`
          )
          amount = limited
        } else {
          amount = parsedValue
        }
      }
    }

    field.onChange({
      amount: amount,
      codeCurrency: field.value?.codeCurrency || 'USD',
    })
  }

  const handleCurrencyChange = (codeCurrency: string) => {
    // Get the current amount value, handling nested structure if it exists
    let currentAmount = 0
    if (field.value?.amount !== undefined) {
      if (typeof field.value.amount === 'number') {
        currentAmount = field.value.amount
      } else if (
        typeof field.value.amount === 'object' &&
        field.value.amount?.amount !== undefined
      ) {
        // Handle nested structure case
        currentAmount = field.value.amount.amount
      }
    }

    field.onChange({
      amount: currentAmount,
      codeCurrency: codeCurrency,
    })
  }

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>
  ): void => {
    // 1. Aserta el tipo de event.target a HTMLInputElement
    const inputElement = event.target as HTMLInputElement

    const allowedKeys = [
      '0',
      '1',
      '2',
      '3',
      '4',
      '5',
      '6',
      '7',
      '8',
      '9',
      '.',
      '-',
      'Tab',
      'Backspace',
      'Delete',
      'ArrowLeft',
      'ArrowRight',
    ]

    if (!allowedKeys.includes(event.key)) {
      event.preventDefault()
    }

    // Ahora `inputElement.value` está correctamente tipado
    if (event.key === '.' && inputElement.value.includes('.')) {
      event.preventDefault()
    }

    if (event.key === '-') {
      if (
        inputElement.value.includes('-') ||
        inputElement.selectionStart !== 0
      ) {
        event.preventDefault()
      }
    }
  }

  return (
    <FormField
      control={control}
      name={name}
      render={() => (
        <FormItem>
          {label && (
            <FormLabel>
              {label}
              {isRequired && ' (*)'}
            </FormLabel>
          )}
          <FormControl>
            <div className="flex w-full gap-1">
              <Input
                className={'w-1/2'}
                min="0"
                onChange={handleAmountChange}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                step="0.0000000001"
                type="number"
                value={
                  field.value?.amount !== undefined &&
                  field.value?.amount !== null
                    ? field.value.amount
                    : ''
                }
              />
              <div className={'w-1/2'}>
                <div className="relative">
                  <Select
                    onValueChange={handleCurrencyChange}
                    value={field.value?.codeCurrency || ''}
                  >
                    <SelectTrigger
                      className={cn(showDoubleChevron && '[&>svg]:invisible')}
                    >
                      <SelectValue placeholder="Moneda" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        {currenciesData.map((currency) => (
                          <SelectItem key={currency.code} value={currency.code}>
                            {currency.displayName}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                  {showDoubleChevron && (
                    <ChevronsUpDown
                      className={
                        'pointer-events-none absolute right-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 opacity-50'
                      }
                    />
                  )}
                </div>
              </div>
            </div>
          </FormControl>
          <FormMessage>{fieldState.error?.message}</FormMessage>
        </FormItem>
      )}
    />
  )
}
